import 'bootstrap/dist/css/bootstrap.css';
import { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Spinner from '../../../shared/spinner';
import { FinanciamentoPromotoraDTO } from '../../../shared/dto/financiamentoPromotoraDTO';
import { Button, Form } from 'react-bootstrap';
import CustomAlert from '../../../shared/customAlert';
import FinanciamentoPromotoraService from '../../../services/financiamentoPromotoraService';
import OriginadorService from '../../../services/originadorService';
import FinanciamentoOriginadorService from '../../../services/financiamentoOriginadorService';
import FinanciamentoService from '../../../services/financiamentoService'; 
import PromotoraService from '../../../services/promotoraService'; 
import { useVerificaPermissao } from '../../../shared/useVerificaPermissao';
import { ProtectedAction } from '../../../AccessControl';
import { OriginadorDTO } from '../../../shared/dto/originadorDTO';
import { Originador } from '../../../shared/model/originador';

type ItemLista = {
  id: string | number;
  codigoDisplay: string | number;
  nome: string;
  ativo: boolean;
  selected: boolean;
  originalRef: any;
};

const ROLE_PROMOTORA_UUID = "7ffffc56-5a2a-44d8-91cb-450af78ad5f0";
const ROLE_ORIGINADOR_UUID = "B6A1331C-A1DD-4972-AF84-9E62E04FF647"; 

function PromotoraEmpregador() {
  useVerificaPermissao();
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'warning' | 'error' } | null>(null);
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  const [tipoOrigem, setTipoOrigem] = useState<'PROMOTORA' | 'ORIGINADOR'>('PROMOTORA');

  const [promotorasDisp, setPromotorasDisp] = useState<ItemLista[]>([]);
  const [promotorasSel, setPromotorasSel] = useState<ItemLista[]>([]);

  const [originadoresDisp, setOriginadoresDisp] = useState<ItemLista[]>([]);
  const [originadoresSel, setOriginadoresSel] = useState<ItemLista[]>([]);

  // Services
  const financiamentoService = new FinanciamentoService(); 
  const financiamentoPromotoraService = new FinanciamentoPromotoraService();
  const originadorService = new OriginadorService();
  const financiamentoOriginadorService = new FinanciamentoOriginadorService();
  const promotoraService = new PromotoraService(); 

  const { idFinanciamento, codigoFinanciamento, nomeFinanciamento } = location.state || {};

  const carregarDados = async () => {
    setLoading(true);
    try {
      const resFin = await financiamentoService.listarPorId(idFinanciamento);
      const finAtual = resFin.data;
      
      let tipoInicial: 'PROMOTORA' | 'ORIGINADOR' = 'PROMOTORA';
      if (finAtual.tipoVinculo && finAtual.tipoVinculo.toUpperCase() === ROLE_ORIGINADOR_UUID.toUpperCase()) {
          tipoInicial = 'ORIGINADOR';
      }
      setTipoOrigem(tipoInicial);

      // --- Carrega Originadores ---
      const resOrig = await originadorService.listarTodosOriginadoresAtivos();
      const listaOrig: OriginadorDTO[] = resOrig.data;

      const resOrigVinculados = await financiamentoOriginadorService.listarFinanciamentoOriginador(idFinanciamento);
      const listaOrigVinculados: OriginadorDTO[] = resOrigVinculados.data;
      const idsVinculados = listaOrigVinculados.map(o => String(o.id));

      const origFormatados: ItemLista[] = listaOrig.map(o => ({
        id: o.id!,
        codigoDisplay: o.id!, 
        nome: o.nome!,
        ativo: false, 
        selected: false,
        originalRef: o
      }));

      setOriginadoresSel(origFormatados.filter(o => idsVinculados.includes(String(o.id))));
      setOriginadoresDisp(origFormatados.filter(o => !idsVinculados.includes(String(o.id))));

      // --- Carrega Promotoras Iniciais (Apenas se for Promotora ou se já houver dados salvos) ---
      const resProm = await financiamentoPromotoraService.listarFinanciamentoPromotora(idFinanciamento);
      const listaProm: FinanciamentoPromotoraDTO[] = resProm.data;

      const promFormatadas: ItemLista[] = listaProm.map(p => ({
        id: p.promotora!,
        codigoDisplay: p.promotora!,
        nome: p.nomePromotora!,
        ativo: p.ativo!,
        selected: false,
        originalRef: p
      }));

      setPromotorasSel(promFormatadas.filter(p => p.ativo));
      setPromotorasDisp(promFormatadas.filter(p => !p.ativo));

    } catch (err) {
      console.error(err);
      setAlert({ message: 'Erro ao carregar dados.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- LÓGICA DE FILTRAR PROMOTORAS ---
  useEffect(() => {
    const filtrarPromotoras = async () => {
        if (tipoOrigem !== 'ORIGINADOR' || loading) return;

        // Se limpar os originadores, limpa as promotoras (para sumir ou limpar o estado)
        if (originadoresSel.length === 0) {
            setPromotorasDisp([]); 
            setPromotorasSel([]); 
            return;
        }

        try {
            const idsOriginadores = originadoresSel.map(o => Number(o.id));
            const res = await promotoraService.buscarPorOriginador(idsOriginadores);
            const promotorasFiltradas: any[] = res.data;

            const idsJaSelecionados = promotorasSel.map(p => String(p.id));

            const novasPromotoras: ItemLista[] = promotorasFiltradas.map(p => {
                const jaSelecionado = idsJaSelecionados.includes(String(p.id));
                return {
                    id: p.id,
                    codigoDisplay: p.id,
                    nome: p.nome,
                    ativo: jaSelecionado,
                    selected: false,
                    originalRef: { 
                        promotora: p.id,
                        nomePromotora: p.nome,
                        ativo: jaSelecionado
                    }
                };
            });

            const novosSelecionados = novasPromotoras.filter(p => p.ativo);
            const novosDisponiveis = novasPromotoras.filter(p => !p.ativo);

            setPromotorasSel(novosSelecionados);
            setPromotorasDisp(novosDisponiveis);

        } catch (error) {
            console.error("Erro ao filtrar promotoras", error);
        }
    };

    filtrarPromotoras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originadoresSel, tipoOrigem]); 

  const RenderDualList = ({
    titulo,
    disponiveis,
    setDisponiveis,
    selecionados,
    setSelecionados
  }: {
    titulo: string,
    disponiveis: ItemLista[],
    setDisponiveis: (l: ItemLista[]) => void,
    selecionados: ItemLista[],
    setSelecionados: (l: ItemLista[]) => void
  }) => {
    
    const moverParaSelecionados = () => {
      const mover = disponiveis.filter(i => i.selected);
      setSelecionados([...selecionados, ...mover.map(i => ({...i, selected: false, ativo: true}))]);
      setDisponiveis(disponiveis.filter(i => !i.selected));
    };

    const moverParaDisponiveis = () => {
      const mover = selecionados.filter(i => i.selected);
      setDisponiveis([...disponiveis, ...mover.map(i => ({...i, selected: false, ativo: false}))]);
      setSelecionados(selecionados.filter(i => !i.selected));
    };

    const moverTudoSel = () => {
      setSelecionados([...selecionados, ...disponiveis.map(i => ({...i, selected: false, ativo: true}))]);
      setDisponiveis([]);
    };

    const moverTudoDisp = () => {
      setDisponiveis([...disponiveis, ...selecionados.map(i => ({...i, selected: false, ativo: false}))]);
      setSelecionados([]);
    };

    const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>, isDisp: boolean) => {
      const ids = Array.from(e.target.selectedOptions).map(opt => String(opt.value));
      const updateFn = isDisp ? setDisponiveis : setSelecionados;
      const list = isDisp ? disponiveis : selecionados;
      
      updateFn(list.map(item => ({
        ...item,
        selected: ids.includes(String(item.id))
      })));
    };

    const listaDispOrd = useMemo(() => [...disponiveis].sort((a,b) => a.nome.localeCompare(b.nome)), [disponiveis]);
    const listaSelOrd = useMemo(() => [...selecionados].sort((a,b) => a.nome.localeCompare(b.nome)), [selecionados]);

    return (
      <div className="card mb-4">
        <div className="card-header bg-light">
          <strong>{titulo}</strong>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            
            <div style={{ display: 'flex', flexDirection: 'column', width: '45%' }}>
              <label className="text-muted">Disponíveis</label>
              <select 
                multiple 
                size={10} 
                className="form-control" 
                onChange={e => handleSelect(e, true)}
                value={listaDispOrd.filter(i => i.selected).map(i => String(i.id))}
              >
                {listaDispOrd.map(item => (
                  <option key={item.id} value={item.id}>
                     {item.nome}
                  </option>
                ))}
              </select>
              <small>Qtd: {listaDispOrd.length}</small>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '5px' }}>
              <Button variant="outline-secondary" size="sm" onClick={moverParaSelecionados}>&gt;</Button>
              <Button variant="outline-secondary" size="sm" onClick={moverParaDisponiveis}>&lt;</Button>
              <Button variant="outline-secondary" size="sm" onClick={moverTudoSel}>&gt;&gt;</Button>
              <Button variant="outline-secondary" size="sm" onClick={moverTudoDisp}>&lt;&lt;</Button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', width: '45%' }}>
              <label className="text-success fw-bold">Selecionados</label>
              <select 
                multiple 
                size={10} 
                className="form-control" 
                onChange={e => handleSelect(e, false)}
                value={listaSelOrd.filter(i => i.selected).map(i => String(i.id))}
              >
                {listaSelOrd.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.nome}
                  </option>
                ))}
              </select>
              <small>Qtd: {listaSelOrd.length}</small>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (tipoOrigem === 'ORIGINADOR') {
      if (originadoresSel.length === 0) {
        setAlert({ message: `Atenção: Para o tipo ${Originador.DESCRITIVO_GERAL}, é obrigatório selecionar ao menos uma ${Originador.DESCRITIVO_GERAL}.`, type: 'warning' });
        return;
      }
      if (promotorasSel.length === 0) {
        setAlert({ message: `Atenção: É obrigatório selecionar ao menos uma Promotora vinculada a ${Originador.DESCRITIVO_GERAL}.`, type: 'warning' });
        return;
      }
    } else {
      if (promotorasSel.length === 0) {
        setAlert({ message: 'Atenção: É obrigatório selecionar ao menos uma Promotora.', type: 'warning' });
        return;
      }
    }

    setLoading(true);
    try {
      const uuidVinculo = tipoOrigem === 'ORIGINADOR' ? ROLE_ORIGINADOR_UUID : ROLE_PROMOTORA_UUID;
      await financiamentoService.atualizarTipoVinculo(idFinanciamento, uuidVinculo);

      const payloadPromotora = [
        ...promotorasDisp.map(p => ({ ...p.originalRef, ativo: false })),
        ...promotorasSel.map(p => ({ ...p.originalRef, ativo: true })),
      ];
      await financiamentoPromotoraService.incluirAlterarFinanciamentoPromotora(idFinanciamento, payloadPromotora);

      let payloadOriginador: any[] = [];
      
      if (tipoOrigem === 'ORIGINADOR') {
        payloadOriginador = [
           ...originadoresDisp.map(o => ({ ...o.originalRef, ativo: false })), 
           ...originadoresSel.map(o => ({ ...o.originalRef, ativo: true })),
        ];
      }
      
      await financiamentoOriginadorService.incluirAlterarFinanciamentoOriginador(idFinanciamento, payloadOriginador);

      setAlert({ message: 'Dados salvos com sucesso!', type: 'success' });
      await carregarDados(); 

    } catch (err) {
      console.error(err);
      setAlert({ message: 'Erro ao salvar os dados!', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleTrocaTipo = (tipo: 'PROMOTORA' | 'ORIGINADOR') => {
      setTipoOrigem(tipo);
      if(tipo === 'PROMOTORA') {
          carregarDados();
      } else {
          setPromotorasDisp([]);
          setPromotorasSel([]);
      }
  }

  // --- LÓGICA DE VISIBILIDADE DO CARD PROMOTORA ---
  const deveMostrarPromotoras = 
      tipoOrigem === 'PROMOTORA' || 
      (tipoOrigem === 'ORIGINADOR' && originadoresSel.length > 0);

  return (
    <div className="position-relative m-md-3">
      {alert && <CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
      {loading ? <Spinner loading={loading} /> : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Cadastro &gt; Vínculos</h5>
            <Button id="App-button-cor-principal" onClick={() => window.history.back()}>Voltar</Button>
          </div>

          <div className="row mb-4">
            <div className="col-md-1"><label>ID</label><input className="form-control" value={idFinanciamento || ''} disabled /></div>
            <div className="col-md-2"><label>Código</label><input className="form-control" value={codigoFinanciamento || ''} disabled /></div>
            <div className="col-md-3"><label>Nome</label><input className="form-control" value={nomeFinanciamento || ''} disabled /></div>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="card p-3 mb-4 bg-light border-0">
              <h6 className="mb-3">Tipo de Vínculo (Esteira):</h6>
              <div className="d-flex gap-4">
                <Form.Check 
                  type="radio" label="Promotora" name="origemRadio" id="origemPromotora"
                  checked={tipoOrigem === 'PROMOTORA'}
                  onChange={() => handleTrocaTipo('PROMOTORA')}
                />
                <Form.Check 
                  type="radio" label="Originador" name="origemRadio" id="origemOriginador"
                  checked={tipoOrigem === 'ORIGINADOR'}
                  onChange={() => handleTrocaTipo('ORIGINADOR')}
                />
              </div>
            </div>

            {tipoOrigem === 'ORIGINADOR' && (
              <RenderDualList 
                titulo="1. Seleção de Originadores"
                disponiveis={originadoresDisp}
                setDisponiveis={setOriginadoresDisp}
                selecionados={originadoresSel}
                setSelecionados={setOriginadoresSel}
              />
            )}

            {/* CONDIÇÃO ADICIONADA AQUI: Só renderiza se for Promotora OU (Originador e tiver item selecionado) */}
            {deveMostrarPromotoras && (
              <RenderDualList 
                titulo={tipoOrigem === 'ORIGINADOR' ? "2. Seleção de Promotoras (Filtrada)" : "Seleção de Promotoras"}
                disponiveis={promotorasDisp}
                setDisponiveis={setPromotorasDisp}
                selecionados={promotorasSel}
                setSelecionados={setPromotorasSel}
              />
            )}

            <div className="mt-4 pb-5">
              <Button type="submit" variant="success" size="lg">
                Salvar Alterações
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}

export default PromotoraEmpregador;